/**
 * Appwrite client for PanelGear HQ.
 *
 * One instance per environment: this app is deployed three times, and each build talks
 * only to its own project. Impersonation and user monitoring are project-scoped, which is
 * the whole reason for that shape — see docs/technical/internal-tools-plan.md §2.5.
 */
import {
  Client,
  Account,
  TablesDB,
  Storage,
  Functions,
  Teams,
  Realtime,
  Channel,
  ID,
  Query,
  Permission,
  Role,
  OAuthProvider,
  ExecutionMethod,
} from 'appwrite';

const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
const projectId = import.meta.env.VITE_APPWRITE_PROJECT;

if (!endpoint || !projectId) {
  // Loud on purpose. A build with these missing produces a page that looks fine and fails
  // on every request, which is a much worse half hour than an error at boot.
  throw new Error(
    'Missing VITE_APPWRITE_ENDPOINT / VITE_APPWRITE_PROJECT. Locally these come from ' +
      'apps/internal/.env.local; in a deployed build scripts/deploy.js injects them.',
  );
}

export const client = new Client().setEndpoint(endpoint).setProject(projectId);

export const account = new Account(client);
export const tablesDB = new TablesDB(client);
export const storage = new Storage(client);
export const functions = new Functions(client);
export const teams = new Teams(client);

/**
 * One Realtime instance for the whole app — it multiplexes every subscription onto a single
 * socket and owns the reconnect/backoff. `client.subscribe()` is deprecated in SDK 26.
 */
export const realtime = new Realtime(client);

export { ID, Query, Permission, Role, OAuthProvider, Channel, ExecutionMethod };

/** The internal-tools database. Not the product database. */
export const DATABASE_ID = 'panelgear-internal';

/** The product database, read only for resolving customers and organisations. */
export const PRODUCT_DATABASE_ID = 'flickquote';

/**
 * The system database: function runs, licences, billing. Read only from here —
 * `function_runs` is granted `read("team:internal")` and nothing more, because
 * every row in it is written by a function holding an API key.
 */
export const SYSTEM_DATABASE_ID = 'flickquote-system';

/** `flickquote-system.function_runs`. See docs/technical/function-runs-conventions.md. */
export const FUNCTION_RUNS_TABLE = 'function_runs';

/**
 * Membership of this team is the whitelist. The id is literal — never ID.unique() — because
 * the `team:internal` permission strings in appwrite.config.json are shared across all three
 * projects. See docs/technical/internal-tools-schema.md §1.
 */
export const INTERNAL_TEAM_ID = 'internal';

export const TABLES = {
  BOARDS: 'boards',
  BOARD_COLUMNS: 'board_columns',
  WORK_ITEMS: 'work_items',
  WORK_ITEM_COMMENTS: 'work_item_comments',
  WORK_ITEM_EVENTS: 'work_item_events',
  WORK_ITEM_CLARIFICATIONS: 'work_item_clarifications',
  /** Row id is the counter's name; see `nextWorkItemNumber` in api/board.ts. */
  COUNTERS: 'counters',
  TICKETS: 'tickets',
  TICKET_MESSAGES: 'ticket_messages',
  CHANGELOG_ENTRIES: 'changelog_entries',
  KB_ARTICLES: 'kb_articles',
  /** Sentry's issue stream, mirrored in by internal-sentry-webhook. See api/sentry.ts. */
  SENTRY_ISSUES: 'sentry_issues',
  /**
   * Written only by the `internal-test-runner` function; HQ reads it. Like the
   * rest of panelgear-internal it is created directly in Appwrite rather than
   * declared in appwrite.config.json — see docs/testing.md.
   */
  TEST_RUNS: 'test_runs',
  /**
   * AI evaluation (HQ #121). The contract is docs/technical/ai-evaluation.md.
   * `eval_cases` soft-deletes through `deletedAt`; the other three are written
   * by the `internal-ai-eval` function and the ai-eval workflow.
   */
  EVAL_CASES: 'eval_cases',
  EVAL_IMPORTS: 'eval_imports',
  EVAL_RUNS: 'eval_runs',
  EVAL_RUN_CASES: 'eval_run_cases',
} as const;

export type TableId = (typeof TABLES)[keyof typeof TABLES];

export const BUCKETS = {
  /** Ticket attachments, written by the inbound mail function as well as by hand. */
  SUPPORT: 'support-attachments',
  /** Anything hung off a work item — specs, screenshots, exports. */
  INTERNAL: 'internal-attachments',
  /** Case photos, per-case run detail and findings files. Read-only from HQ; only the workflow's key writes. */
  AI_EVAL: 'ai-eval',
} as const;

/**
 * Realtime channels, via the SDK's typed builder rather than hand-written strings — the
 * legacy `databases.*.collections.*.documents` form is not what TablesDB publishes on.
 */
export const tableChannel = (tableId: TableId | string, databaseId: string = DATABASE_ID) =>
  Channel.tablesdb(databaseId).table(tableId).row().toString();

export const rowChannel = (
  tableId: TableId | string,
  rowId: string,
  databaseId: string = DATABASE_ID,
) => Channel.tablesdb(databaseId).table(tableId).row(rowId).toString();
